/**
 * What the screen mark is actually being painted on top of.
 *
 * `StaffScreenMark` has to choose an ink polarity: white dots on a dark page, dark ink on a light
 * one. It used to take that from the theme the app believes it is in, and that is a different
 * question from what the browser finally paints. Anything that re-grounds the page without telling
 * the app turns the mark inside out — and because the tile itself is untouched, nothing looks
 * broken. The mark is still there, still the right shape, and worth nothing.
 *
 * The case that found this is Dark Reader, which panel staff run so that *other* sites have a dark
 * mode and which therefore stays switched on over this one. It leaves the tile alone: in dynamic
 * mode the tile is a data URL of near-transparent pixels, which its image analysis classes as
 * neither dark nor light and passes through untouched. What it does instead is set
 * `html { background-color: #181a1b !important }`. So in light theme the app goes on painting dark
 * ink onto a surface that is now dark. Measured against unmarked screenshots of the same page, at
 * 1280x960, 1x:
 *
 * | theme | Dark Reader | mark amplitude | 320px clippings read at JPEG q70 |
 * | --- | --- | --- | --- |
 * | dark | off | 3.96 levels | 2 of 4 |
 * | dark | dynamic | 4.00 levels | 2 of 4 |
 * | light | off | 4.86 levels | 3 of 4 |
 * | light | dynamic | **1.01 levels** | **0 of 4** |
 *
 * Dark theme is unharmed — white ink on `#181a1b` is the polarity it already wanted. Light theme
 * loses four fifths of its amplitude and stops reading. Nothing removed the mark; the ground moved
 * out from under it.
 *
 * So the polarity is measured rather than assumed. Extensions, user stylesheets, a print sheet and
 * `forced-colors` all land in the computed styles this reads, which is the only place where what is
 * about to be on screen can be known. What it cannot see is a ground painted by something other
 * than `background-color` — a gradient, an image, the galaxy canvas — or one an ancestor's `opacity`
 * or `filter` has since moved. Those are read as the colour underneath them, which is the right
 * answer for the panel's own surfaces and an approximate one in general. A measurement that cannot
 * be made at all comes back `null` so the caller can fall back to the theme rather than guess.
 */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0–1. */
  a: number;
}

/** Chrome's canvas colour when `color-scheme` puts the page in dark mode. */
const DARK_CANVAS: Rgba = { r: 18, g: 18, b: 18, a: 1 };
const LIGHT_CANVAS: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/**
 * A computed `background-color` as a colour, or `null` for one this does not understand.
 *
 * Only `rgb()` and `rgba()` are parsed, because those are what every engine serialises a computed
 * `background-color` to whatever was authored — a hex, a named colour, `oklch()`, a relative colour.
 * Anything else is a form nobody has seen come out of `getComputedStyle`, and a colour guessed at
 * wrongly here would flip the ink on a whole page, so it is declined instead.
 */
export function parseColor(value: string): Rgba | null {
  const text = value.trim();
  if (text === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  // The alpha group takes a sign so that an out-of-range one reaches the clamp below rather than
  // failing the match and being reported as a colour nobody could read.
  const match = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*(-?[\d.]+%?))?\s*\)$/i.exec(text);
  if (!match) return null;
  const [, r, g, b, alpha] = match;
  const a = alpha === undefined ? 1 : alpha.endsWith('%') ? Number(alpha.slice(0, -1)) / 100 : Number(alpha);
  if ([r, g, b, String(a)].some((part) => !Number.isFinite(Number(part)))) return null;
  return { r: Number(r), g: Number(g), b: Number(b), a: Math.min(1, Math.max(0, a)) };
}

/** sRGB relative luminance, 0–1. The same weighting the reader folds pixels with. */
export function luminance(color: Pick<Rgba, 'r' | 'g' | 'b'>): number {
  return (0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b) / 255;
}

/** `over` painted onto `under`, source-over. */
export function composite(over: Rgba, under: Rgba): Rgba {
  if (over.a >= 1) return over;
  if (over.a <= 0) return under;
  const a = over.a + under.a * (1 - over.a);
  if (a <= 0) return { r: 0, g: 0, b: 0, a: 0 };
  const mix = (top: number, bottom: number) => (top * over.a + bottom * under.a * (1 - over.a)) / a;
  return { r: mix(over.r, under.r), g: mix(over.g, under.g), b: mix(over.b, under.b), a };
}

/**
 * Folds a stack of computed background colours onto an opaque base.
 *
 * `stack` is in the order `elementsFromPoint` hands them back — topmost first — so it is walked
 * from the far end. Values this cannot parse are skipped rather than treated as transparent: a
 * colour that was not understood is not evidence that nothing was painted.
 */
export function flattenStack(stack: string[], base: Rgba): Rgba {
  let result = base;
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const color = parseColor(stack[index]);
    if (color) result = composite(color, result);
  }
  return result;
}

/**
 * Ground luminance at which the ink changes polarity.
 *
 * The ink is whichever direction has more room to move, so the crossover is the midpoint. This is
 * not a finely tuned number and should not be treated as one: the panel's own grounds measure 0.02
 * (dark) and 0.93 (light), and Dark Reader's is 0.01, so every surface in play is nowhere near it.
 * It matters only for a mid-grey page, which is the one case where neither polarity is good and the
 * mark is weak whatever is chosen.
 */
export const INK_FLIP_LUMINANCE = 0.5;

/** Which of the two inks belongs on a ground of this luminance. */
export function inkForGround(groundLuminance: number): 'onLight' | 'onDark' {
  return groundLuminance > INK_FLIP_LUMINANCE ? 'onLight' : 'onDark';
}

/**
 * Where in the viewport to sample, as fractions of its width and height.
 *
 * Nine points on an inset grid. One point is not enough — it lands on a card, a table row or a
 * heading as often as on the page itself — and the median of nine costs nothing. Inset from the
 * edges because the outermost pixels are chrome, scrollbar gutter and sticky headers.
 */
const SAMPLE_FRACTIONS = [0.15, 0.5, 0.85];

/** The colour the page would show through everything transparent, which depends on `color-scheme`. */
function canvasBase(root: Element): Rgba {
  const scheme = getComputedStyle(root).colorScheme.trim().toLowerCase();
  return scheme === 'dark' || scheme === 'only dark' ? DARK_CANVAS : LIGHT_CANVAS;
}

/**
 * The luminance of the surface under the mark, or `null` if it cannot be measured here.
 *
 * `null` means the DOM gave nothing to measure — no viewport, no elements at a sample point — not
 * that the page is dark. The caller falls back to the theme in that case, which is where the theme
 * flag is genuinely the best answer available.
 *
 * The mark's own layer never appears in the samples: `pointer-events: none` takes it out of hit
 * testing, which is also why the IP watermark's layer cannot shadow this.
 */
export function measureGroundLuminance(): number | null {
  if (typeof document === 'undefined' || !document.body) return null;
  const width = window.innerWidth;
  const height = window.innerHeight;
  if (!width || !height) return null;

  const base = canvasBase(document.documentElement);
  const readings: number[] = [];
  for (const fx of SAMPLE_FRACTIONS) {
    for (const fy of SAMPLE_FRACTIONS) {
      const stack = document.elementsFromPoint(Math.round(width * fx), Math.round(height * fy));
      if (!stack.length) continue;
      const colors = stack.map((element) => getComputedStyle(element).backgroundColor);
      readings.push(luminance(flattenStack(colors, base)));
    }
  }
  if (!readings.length) return null;
  readings.sort((a, b) => a - b);
  return readings[Math.floor(readings.length / 2)];
}
